import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import AccordionController from 'components/AccordionController';
import Section from 'components/Section';
import TextField from 'components/TextField';
import Form from 'components/Form';
import Checkbox from 'components/Checkbox';

import { useErrorHandler } from 'lib';
import { UserEditForm_user$key } from '__generated__/UserEditForm_user.graphql';
import { UserEditFormMutation } from '__generated__/UserEditFormMutation.graphql';
import { useCurrentUser } from 'components/CurrentUserProvider';

type FormValues = {
  name: string;
  email: string;
  canPublish: boolean;
};

type Props = {
  user: UserEditForm_user$key;
};

const UserEditForm: FC<Props> = props => {
  const errorHandler = useErrorHandler();
  const currentUser = useCurrentUser();

  const { id, ...initial } = useFragment(
    graphql`
      fragment UserEditForm_user on User {
        id
        name
        email
        canPublish
      }
    `,
    props.user
  );

  const [update, loading] = useMutation<UserEditFormMutation>(graphql`
    mutation UserEditFormMutation($id: ID!, $input: UpdateUserInput!) {
      updateUser(id: $id, input: $input) {
        ...UserEditForm_user
      }
    }
  `);

  const handleSubmit = useCallback(
    (input: FormValues) => {
      update({
        variables: { id, input },
        ...errorHandler({
          successMessage: 'Išsaugota',
        }),
      });
    },
    [errorHandler, id, update]
  );

  return (
    <Formik<FormValues> initialValues={initial} onSubmit={handleSubmit}>
      <Form loading={loading}>
        <AccordionController id="userEdit">
          <Section id="main" title="Bendra informacija">
            <Stack spacing={4}>
              <TextField autoFocus name="name" label="Vardas" fullWidth />
              <TextField name="email" label="El. paštas" fullWidth />

              <FormControlLabel
                control={
                  <Checkbox
                    name="canPublish"
                    disabled={!currentUser.canPublish}
                  />
                }
                label="Gali publikuoti"
              />
            </Stack>
          </Section>
        </AccordionController>
      </Form>
    </Formik>
  );
};

export default UserEditForm;
